/**
 * EquipmentController — /api/v1/equipment
 * Mirrors: src/Aidepo.Api/Controllers/EquipmentController.cs
 */
import { apiDelete, apiPatch, apiPost } from '../client.js';
import type { Uuid } from '../types.js';

export interface AddEquipmentRequest {
  readonly name: string;
  readonly brand?: string;
  readonly model?: string;
  readonly category?: string;
  readonly roomId?: Uuid;
  readonly roomNameHint?: string;
  readonly propertyId?: Uuid;
  readonly propertyNameHint?: string;
  /**
   * Manually picked icon name (hh-345). Omit or pass null to auto-resolve
   * the icon from the category / name.
   */
  readonly icon?: string | null;
}

export interface EquipmentResponse {
  readonly id: Uuid;
  readonly name: string;
  readonly brand: string | null;
  readonly model: string | null;
  readonly category: string;
  /** Resolved icon name, or null when left to auto-resolve (hh-345). */
  readonly icon?: string | null;
}

export interface UpdateEquipmentRequest {
  readonly name?: string;
  readonly brand?: string;
  readonly model?: string;
  readonly category?: string;
  /**
   * Icon override (hh-345). Field absent → not changed; non-empty string →
   * manual icon set; `null` → reset to auto-resolve.
   */
  readonly icon?: string | null;
}

/** POST /api/v1/equipment — add equipment (filter, boiler, AC, …). */
export function addEquipment(req: AddEquipmentRequest): Promise<EquipmentResponse> {
  return apiPost<EquipmentResponse, AddEquipmentRequest>('/api/v1/equipment', req);
}

/** PATCH /api/v1/equipment/{id}. */
export function updateEquipment(id: Uuid, req: UpdateEquipmentRequest): Promise<EquipmentResponse> {
  return apiPatch<EquipmentResponse, UpdateEquipmentRequest>(`/api/v1/equipment/${id}`, req);
}

/** Перенос оборудования: ровно один из roomId / propertyId. */
export interface MoveEquipmentRequest {
  readonly roomId?: Uuid;
  readonly propertyId?: Uuid;
}

export interface MoveEquipmentResponse {
  readonly id: Uuid;
  readonly roomId: Uuid | null;
  readonly propertyId: Uuid | null;
}

/** POST /api/v1/equipment/{id}/move — перенести оборудование в другую локацию. */
export function moveEquipment(
  id: Uuid,
  req: MoveEquipmentRequest,
): Promise<MoveEquipmentResponse> {
  return apiPost<MoveEquipmentResponse, MoveEquipmentRequest>(
    `/api/v1/equipment/${id}/move`,
    req,
  );
}

/** DELETE /api/v1/equipment/{id}. */
export function deleteEquipment(id: Uuid): Promise<void> {
  return apiDelete(`/api/v1/equipment/${id}`);
}

// ─────────────────────────────────────────────────────────
// Schedule diff (Phase 3.8) — POST /api/v1/equipment/{id}/schedule/diff
//   compares the catalog/proposed regulation against current rules.
//   Backend currently returns a structured STUB (stub: true); shape is final.
// ─────────────────────────────────────────────────────────

/** A rule the diff proposes to add. */
export interface ScheduleDiffAdd {
  readonly title: string;
  readonly periodMonths: number;
  readonly isSafetyCritical: boolean;
}

/** A rule whose period the diff proposes to change. */
export interface ScheduleDiffModify {
  readonly ruleId: Uuid;
  readonly title: string;
  readonly fromPeriodMonths: number;
  readonly toPeriodMonths: number;
  readonly isSafetyCritical: boolean;
}

/** A rule the diff proposes to remove (safety-critical rules are never listed here). */
export interface ScheduleDiffRemove {
  readonly ruleId: Uuid;
  readonly title: string;
  readonly isSafetyCritical: boolean;
}

export interface ScheduleDiffResponse {
  readonly equipmentId: Uuid;
  readonly stub: boolean;
  readonly note: string;
  readonly add: readonly ScheduleDiffAdd[];
  readonly modify: readonly ScheduleDiffModify[];
  readonly remove: readonly ScheduleDiffRemove[];
}

/** Operation kind for applying a single diff entry. */
export type ScheduleDiffOp = 'add' | 'modify' | 'remove';

/** One apply request — fields required depend on `op`. */
export interface ApplyScheduleDiffRequest {
  readonly op: ScheduleDiffOp;
  readonly ruleId?: Uuid;
  readonly title?: string;
  readonly periodMonths?: number;
  readonly isSafetyCritical?: boolean;
}

export interface ApplyScheduleDiffResponse {
  readonly op: ScheduleDiffOp;
  readonly ruleId?: Uuid;
  readonly changeRecordId?: Uuid;
}

/** POST /api/v1/equipment/{id}/schedule/diff — regulation diff (add/modify/remove). */
export function scheduleDiff(id: Uuid, catalogType?: string): Promise<ScheduleDiffResponse> {
  return apiPost<ScheduleDiffResponse, { catalogType?: string }>(
    `/api/v1/equipment/${id}/schedule/diff`,
    catalogType !== undefined ? { catalogType } : {},
  );
}

/** POST /api/v1/equipment/{id}/schedule/diff/apply — apply one diff entry. */
export function applyScheduleDiff(
  id: Uuid,
  req: ApplyScheduleDiffRequest,
): Promise<ApplyScheduleDiffResponse> {
  return apiPost<ApplyScheduleDiffResponse, ApplyScheduleDiffRequest>(
    `/api/v1/equipment/${id}/schedule/diff/apply`,
    req,
  );
}
